<!--
  The selected box: its class, its flags, its provenance, and its geometry.

  `uncertain` and `restored` are toggles here because they are per-box **flags** with exactly
  two states. They are never letter classes: folding a mirrored form into its own class would
  double the class count, halve the data per class, and destroy the direction signal the
  chirality head is meant to predict.

  ---------------------------------------------------------------------------
  The handedness picker
  ---------------------------------------------------------------------------
  There was a third checkbox here, `mirrored`, and it was ticked or not before anybody looked:
  every box was born with `mirrored = (reading direction is ltr)`. So "I judged this not
  mirrored" and "I never looked" were the same stored byte, and on a record where nobody
  overrode it the column was fully derivable from the reading direction and carried no
  information at all.

  Five options replace it, because the question genuinely has five answers — including
  *nobody has looked*, which is what every new box says. Nothing derives any of them; the one
  value set without a human is `symmetric`, and it comes from measuring each stored reference
  form against its own mirror.

  **Nothing prompts it.** No meter counts chirality and none may grow a term for it: the
  picker is here so an annotator who can plainly see a mirrored glyph can say so, not so the
  tool can ask them about every box.

  Geometry is displayed to two decimals but stored at full float precision — the numbers shown
  are a readout, not a rounding step.

  ---------------------------------------------------------------------------
  The class picker
  ---------------------------------------------------------------------------
  Chips were previously the *only* class control in the interface, which meant an insertion
  could only ever carry a class that appears somewhere in this inscription's transliteration.
  A glyph whose letter OCIANA does not record anywhere in the inscription — the divider case,
  or a letter the editor missed — was therefore unlabellable, and one of the three
  image-vs-text disagreement cases was unreachable. Mismatch handling is meant to be
  first-class, so the picker is here: thirty classes, filterable, and reachable from the
  keyboard with `C`.
-->
<script lang="ts">
  import { untrack } from 'svelte';

  import type { VocabEntryOut } from '../lib/api/types';
  import { CHIRALITY_OPTIONS, type ChiralityOption } from '../lib/draft';
  import { glyphOrientation, letterChirality } from '../lib/glyphs';
  import { blurOnPointerActivation } from '../lib/keymap';
  import { app } from '../lib/stores/app.svelte';
  import { glyphPref } from '../lib/stores/glyphPref.svelte';
  import { session } from '../lib/stores/session.svelte';
  import GlyphImage from './GlyphImage.svelte';

  interface Props {
    /** Bumped by the workspace to request keyboard focus on the filter (the `C` shortcut). */
    focusToken?: number;
  }

  const { focusToken = 0 }: Props = $props();

  const box = $derived(session.selected);
  const token = $derived(
    box?.expectedTokenIndex !== null && box?.expectedTokenIndex !== undefined
      ? session.tokens[box.expectedTokenIndex]
      : undefined,
  );

  /** Same orientation the chip strip draws in. See `glyphOrientation`. */
  const orientation = $derived(glyphOrientation(session.readingDirection));

  /**
   * Whether this box's class has a handedness to record — three-valued, and the third value
   * is not a falsy version of the other two. See `letterChirality`.
   */
  const disposition = $derived(box ? letterChirality(box.letterClass) : null);

  /**
   * The two candidate forms have nothing to name on a class with no reference form.
   *
   * `illegible` and `unknown` have none and never will, so "matches the reference form" has
   * no referent there. The options still render — the list is the same five everywhere, and a
   * disappearing option is harder to reason about than a refused one — but they are refused,
   * with the reason stated below. `indeterminate` and `not assessed` stay available: those two
   * are about the annotator, not about the artifact.
   */
  function unavailable(option: ChiralityOption): boolean {
    return option.form !== null && disposition === null;
  }

  let filter = $state('');
  let filterInput = $state<HTMLInputElement | null>(null);
  // Seeded from the incoming prop so mounting is not itself a focus request. The initial value
  // is exactly what is wanted, hence `untrack` — same pattern as `ImageCanvas`'s fit token.
  let lastFocusToken = untrack(() => focusToken);

  /**
   * Match on the canonical name, the display form and the id.
   *
   * Canonical *and* display because the two differ where it matters: an annotator looking for
   * `s¹` will type `s1`, and one looking for `illegible` will not type `—`.
   */
  function matches(entry: VocabEntryOut, needle: string): boolean {
    if (!needle) return true;
    const q = needle.trim().toLowerCase();
    if (!q) return true;
    return (
      entry.letter.toLowerCase().includes(q) ||
      entry.display.toLowerCase().includes(q) ||
      entry.kind.toLowerCase().includes(q) ||
      String(entry.id) === q
    );
  }

  const letters = $derived(app.letterClasses.filter((entry) => matches(entry, filter)));
  const specials = $derived(app.specialClasses.filter((entry) => matches(entry, filter)));
  const firstMatch = $derived(letters[0] ?? specials[0]);

  function choose(entry: VocabEntryOut, event?: MouseEvent): void {
    if (!box) return;
    session.setClass(box.key, entry);
    filter = '';
    if (event) blurOnPointerActivation(event);
  }

  function onFilterKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' && firstMatch) {
      event.preventDefault();
      choose(firstMatch);
      filterInput?.blur();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      filter = '';
      filterInput?.blur();
    }
  }

  $effect(() => {
    const requested = focusToken;
    if (requested === lastFocusToken) return;
    lastFocusToken = requested;
    filterInput?.focus();
    filterInput?.select();
  });
</script>

<div class="panel col inspector">
  <div class="row">
    <h3>Selected box</h3>
    <span class="spacer"></span>
    {#if box}
      <button
        class="danger"
        onclick={() => session.deleteBox(box.key)}
        disabled={session.readOnly}
      >
        Delete <kbd>Del</kbd>
      </button>
    {/if}
  </div>

  {#if !box}
    <p class="muted small">
      Nothing selected. Click a box on the image, or press <kbd>Tab</kbd> to step through them.
    </p>
  {:else}
    <div class="row wrap">
      <span class="glyph big">{box.display ?? `#${box.letterClass}`}</span>
      {#if glyphPref.show}
        <GlyphImage
          letterClass={box.letterClass}
          mirrored={orientation.mirrored}
          turned={orientation.turned}
          height={36}
          label={box.display ?? `class ${box.letterClass}`}
        />
      {/if}
      <div class="col small">
        <span>
          {#if box.alignment === 'expected'}
            expected letter {(box.expectedTokenIndex ?? 0) + 1}
          {:else}
            <strong class="unexpected">unexpected letter</strong> — a glyph OCIANA's reading does
            not contain
          {/if}
        </span>
        <span class="muted mono">
          class {box.letterClass} · {app.byId.get(box.letterClass)?.letter ?? '?'} · source
          {box.source}
        </span>
      </div>
    </div>

    <div class="row wrap flags">
      <label class="toggle">
        <input
          type="checkbox"
          checked={box.uncertain}
          disabled={session.readOnly}
          onchange={() => session.toggleFlag(box.key, 'uncertain')}
        />
        uncertain <kbd>U</kbd>
      </label>
      <label class="toggle">
        <input
          type="checkbox"
          checked={box.restored}
          disabled={session.readOnly}
          onchange={() => session.toggleFlag(box.key, 'restored')}
        />
        restored <kbd>R</kbd>
      </label>
    </div>

    {#if token && (token.uncertain !== box.uncertain || token.restored !== box.restored)}
      <p class="small muted">
        The editor marked this letter
        {token.uncertain ? 'uncertain' : ''}{token.uncertain && token.restored ? ' and ' : ''}{token.restored
          ? 'restored'
          : ''}{!token.uncertain && !token.restored ? 'neither uncertain nor restored' : ''}.
        Your reading differs, and both are kept.
      </p>
    {/if}

    <div class="col small picker chirality">
      <div class="row wrap">
        <strong>Handedness</strong>
        <kbd>M</kbd>
        <span class="spacer"></span>
        <span class="muted">relative to the stored reference form</span>
      </div>

      <div class="class-grid chirality-grid" role="group" aria-label="Box handedness">
        {#each CHIRALITY_OPTIONS as option (option.label)}
          <button
            type="button"
            class="class-chip wide chirality-chip"
            class:current={box.chirality === option.value}
            aria-pressed={box.chirality === option.value}
            disabled={session.readOnly || unavailable(option)}
            title={unavailable(option)
              ? `No reference form for this class, so “${option.label}” has nothing to name`
              : option.hint}
            onclick={(event) => {
              session.setChirality(box.key, option.value);
              blurOnPointerActivation(event);
            }}
          >
            <span class="col chirality-text">
              <span>{option.label}</span>
              <span class="muted hint-line">{option.hint}</span>
            </span>
            <!--
              `mirrored={option.form}`, deliberately NOT composed with `orientation.mirrored`.
              Every other form on this screen is drawn in the *record's* frame; these two are
              the two things being chosen between, and they are named in the **artifact's**
              frame. Composing them would swap the two options' labels on an `ltr` record,
              which is the derivation this column exists to end, reappearing as a rendering
              bug. `turned` is kept — a quarter turn does not change a handedness.
            -->
            {#if glyphPref.show && option.form !== null}
              <GlyphImage
                letterClass={box.letterClass}
                mirrored={option.form}
                turned={orientation.turned}
                height={26}
                label={option.label}
              />
            {/if}
          </button>
        {/each}
      </div>

      {#if disposition === 'symmetric'}
        <p class="muted hint">
          This letter form reads the same either way, so there is no handedness to record — the
          box is stored <strong>symmetric</strong> without anybody being asked, and <kbd>M</kbd>
          declines rather than cycling. Change it here if you disagree with the measurement.
        </p>
      {:else if disposition === null}
        <p class="muted hint">
          There is no reference form for this class, so <strong>reference</strong> and
          <strong>mirrored</strong> have nothing to be measured against and are not offered.
          <strong>Indeterminate</strong> still is, for a glyph you looked at and could not call.
        </p>
      {:else}
        <p class="muted hint">
          <strong>Not assessed</strong> is what every new box says, and it is never read as
          "matches the reference form" — leave it there whenever you cannot plainly see which
          way the glyph faces. Nothing counts this as outstanding work.
          {#if session.unjudgedChiralityCount}
            {session.unjudgedChiralityCount} box(es) on this image are unassessed.
          {/if}
        </p>
      {/if}
    </div>

    <div class="col small picker">
      <div class="row wrap">
        <strong>Class</strong>
        <span class="spacer"></span>
        <span class="muted">
          {#if session.tokens.length}
            for a glyph the transliteration does not contain — chips cover the rest
          {:else}
            this record has no transliteration, so every box's class is set here
          {/if}
        </span>
      </div>
      <input
        type="search"
        placeholder="Filter classes — try s1, ʿ, illegible, divider  (C)"
        bind:this={filterInput}
        bind:value={filter}
        disabled={session.readOnly}
        onkeydown={onFilterKey}
        aria-label="Filter letter classes"
      />
      {#if app.vocab === null}
        <p class="muted">The vocabulary has not loaded, so no class can be set.</p>
      {:else}
        <div class="class-grid" role="group" aria-label="Letter classes">
          {#each letters as entry (entry.id)}
            <button
              type="button"
              class="class-chip"
              class:current={entry.id === box.letterClass}
              class:with-glyph={glyphPref.show}
              aria-pressed={entry.id === box.letterClass}
              disabled={session.readOnly}
              title={`${entry.display} — vocabulary class ${entry.id} (${entry.letter})`}
              onclick={(event) => choose(entry, event)}
            >
              <span class="chip-text">
                <span class="glyph">{entry.display}</span>
                <span class="mono muted id">{entry.id}</span>
              </span>
              {#if glyphPref.show}
                <GlyphImage
                  letterClass={entry.id}
                  mirrored={orientation.mirrored}
                  turned={orientation.turned}
                  height={26}
                  label={entry.display}
                />
              {/if}
            </button>
          {/each}
        </div>
        {#if specials.length}
          <div class="class-grid special" role="group" aria-label="Non-letter classes">
            {#each specials as entry (entry.id)}
              <button
                type="button"
                class="class-chip wide"
                class:current={entry.id === box.letterClass}
                aria-pressed={entry.id === box.letterClass}
                disabled={session.readOnly}
                title={`${entry.letter} — vocabulary class ${entry.id}`}
                onclick={(event) => choose(entry, event)}
              >
                <span>{entry.letter}</span>
                <span class="mono muted id">{entry.id}</span>
              </button>
            {/each}
          </div>
        {/if}
        {#if letters.length === 0 && specials.length === 0}
          <p class="muted">No class matches “{filter}”.</p>
        {/if}
      {/if}
      <p class="muted hint">
        Setting a class that is not the class of this box's chip records it as an
        <strong>unexpected letter</strong> — the honest description of "OCIANA reads one letter
        here and the stone shows another". Mark the chip not present (<kbd>N</kbd>) as well if
        the expected letter really is absent. Never pick a letter you know to be wrong just to
        get a class onto a box.
      </p>
    </div>

    <label class="col small">
      Notes
      <textarea
        rows="2"
        disabled={session.readOnly}
        value={box.notes ?? ''}
        onchange={(event) =>
          session.patchBox(box.key, { notes: event.currentTarget.value || null })}
      ></textarea>
    </label>

    <div class="small muted mono geometry">
      x {box.bbox.x.toFixed(2)} · y {box.bbox.y.toFixed(2)} · w {box.bbox.w.toFixed(2)} · h
      {box.bbox.h.toFixed(2)} (image pixels)
    </div>

    <div class="small muted">
      {#if box.id === null}
        not yet saved
      {:else}
        annotation #{box.id}
      {/if}
      {#if box.confidence !== null}
        · confidence {box.confidence.toFixed(3)} (calibrated, model-sourced)
      {/if}
    </div>
  {/if}

  {#if session.notices.length}
    <div class="banner warn small">
      {#each session.notices as notice, index (index)}
        <p>{notice}</p>
      {/each}
    </div>
  {/if}
</div>

<style>
  .inspector {
    gap: 10px;
  }

  .big {
    font-size: 2.4rem;
    line-height: 1;
  }

  .flags {
    gap: 14px;
  }

  .toggle {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: var(--text);
  }

  .unexpected {
    color: var(--unexpected);
  }

  .geometry {
    word-break: break-word;
  }

  .picker {
    gap: 6px;
    border-top: 1px solid var(--line);
    padding-top: 8px;
  }

  .class-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    max-height: 24vh;
    overflow-y: auto;
  }

  .class-grid.special {
    max-height: none;
  }

  .class-chip {
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
    min-width: 34px;
    padding: 2px 6px;
    background: var(--bg-raised);
    border: 1px solid var(--line-strong);
    border-radius: 5px;
  }

  /*
   * With the reference form showing, the chip stacks: transliteration and class id on top, the
   * form under them — the same reading order as the chip strip, so the two palettes teach the
   * same pairing. There is no form for `illegible` / `unknown`, and the specials grid below
   * deliberately shows no empty tile: every entry there is formless, so a row of dashed boxes
   * would be noise rather than the honest gap it is on a letter chip.
   */
  .class-chip.with-glyph {
    flex-direction: column;
    align-items: center;
    gap: 2px;
    padding: 3px 6px 4px;
  }

  .chip-text {
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
  }

  .class-chip .glyph {
    font-size: 1.15rem;
  }

  .class-chip .id {
    font-size: var(--text-xs);
  }

  /* One per line: the options carry a gloss each, and a wrapped row of five put the two
     candidate forms at different heights — which is the one comparison this control exists to
     make easy. */
  .chirality-grid {
    flex-direction: column;
    max-height: none;
  }

  .chirality-chip {
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    text-align: left;
  }

  .chirality-text {
    gap: 0;
    min-width: 0;
  }

  .hint-line {
    font-size: var(--text-xs);
  }

  /* Current class: border weight + a filled background + aria-pressed, never colour alone. */
  .class-chip.current {
    border-width: 2px;
    border-color: var(--accent);
    background: var(--accent-dim);
  }

  .hint {
    margin: 0;
  }

  textarea {
    resize: vertical;
  }

  .banner p {
    margin: 0 0 4px;
  }
</style>
